import React, { useState } from 'react';
import { Languages, GraduationCap, ArrowRight, CheckCircle2, BookOpen } from 'lucide-react';
import { OpcionesGeneracionPsico } from '../../data/mallaPsico2013';

interface ConfiguracionPsicoProps {
  onConfirm: (opciones: OpcionesGeneracionPsico) => void;
  onBack?: () => void;
}

export const ConfiguracionPsico: React.FC<ConfiguracionPsicoProps> = ({ onConfirm, onBack }) => {
  const [idioma, setIdioma] = useState<'ingles' | 'portugues'>('ingles');
  const [esPlan1988, setEsPlan1988] = useState<boolean>(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm({ idioma, esPlan1988 });
  };

  return (
    <div className="w-full max-w-xl mx-auto bg-white/60 backdrop-blur-xl border border-white/80 rounded-[2rem] p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.06)] space-y-6 animate-in fade-in duration-300 font-sans select-none">
      {/* Cabecera del Wizard */}
      <div className="text-center space-y-2">
        <div className="w-12 h-12 rounded-2xl bg-[#0099A9]/10 text-[#0099A9] flex items-center justify-center mx-auto shadow-xs">
          <GraduationCap size={24} />
        </div>
        <h2 className="text-xl font-extrabold text-slate-900 tracking-tight">
          Configura tu Trayectoria en Psicología
        </h2>
        <p className="text-xs text-slate-500 max-w-md mx-auto leading-relaxed">
          Personaliza los requerimientos de tu Plan de Estudios 2013 antes de cargar tu malla curricular.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Decisión 1: Idioma Transversal */}
        <div className="space-y-3">
          <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
            <Languages size={15} className="text-[#0099A9]" />
            1. Idioma Extranjero a Acreditar
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Opción Inglés */}
            <div
              onClick={() => setIdioma('ingles')}
              className={`p-4 rounded-2xl border-2 transition-all cursor-pointer flex items-center justify-between ${
                idioma === 'ingles'
                  ? 'border-[#0099A9] bg-[#E6F5F6] text-[#007683] shadow-xs'
                  : 'border-slate-200/80 bg-white/80 hover:bg-slate-50/80 text-slate-700'
              }`}
            >
              <div className="space-y-0.5">
                <span className="font-bold text-xs block">Inglés Técnico</span>
                <span className="text-[11px] text-slate-500 block">COD 643G • 5 Créditos</span>
              </div>
              {idioma === 'ingles' && <CheckCircle2 size={18} className="text-[#0099A9] shrink-0" />}
            </div>

            {/* Opción Portugués */}
            <div
              onClick={() => setIdioma('portugues')}
              className={`p-4 rounded-2xl border-2 transition-all cursor-pointer flex items-center justify-between ${
                idioma === 'portugues'
                  ? 'border-[#0099A9] bg-[#E6F5F6] text-[#007683] shadow-xs'
                  : 'border-slate-200/80 bg-white/80 hover:bg-slate-50/80 text-slate-700'
              }`}
            >
              <div className="space-y-0.5">
                <span className="font-bold text-xs block">Portugués Técnico</span>
                <span className="text-[11px] text-slate-500 block">COD 644G • 5 Créditos</span>
              </div>
              {idioma === 'portugues' && <CheckCircle2 size={18} className="text-[#0099A9] shrink-0" />}
            </div>
          </div>
        </div>

        {/* Decisión 2: Perfil Plan 1988 */}
        <div className="space-y-3">
          <label className="block text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
            <BookOpen size={15} className="text-[#0099A9]" />
            2. ¿Perteneces a la Migración del Plan 1988?
          </label>

          <div className="space-y-2.5">
            {/* Opción Ingreso Directo */}
            <div
              onClick={() => setEsPlan1988(false)}
              className={`p-4 rounded-2xl border-2 transition-all cursor-pointer flex items-center justify-between ${
                !esPlan1988
                  ? 'border-[#0099A9] bg-[#E6F5F6] text-[#007683] shadow-xs'
                  : 'border-slate-200/80 bg-white/80 hover:bg-slate-50/80 text-slate-700'
              }`}
            >
              <div className="space-y-0.5">
                <span className="font-bold text-xs block">No, Ingreso Directo (Plan 2013 Puro)</span>
                <span className="text-[11px] text-slate-500 block">Comenzaré con todas las UCOs sin iniciar.</span>
              </div>
              {!esPlan1988 && <CheckCircle2 size={18} className="text-[#0099A9] shrink-0" />}
            </div>

            {/* Opción Migración Plan 1988 */}
            <div
              onClick={() => setEsPlan1988(true)}
              className={`p-4 rounded-2xl border-2 transition-all cursor-pointer flex items-center justify-between ${
                esPlan1988
                  ? 'border-[#0099A9] bg-[#E6F5F6] text-[#007683] shadow-xs'
                  : 'border-slate-200/80 bg-white/80 hover:bg-slate-50/80 text-slate-700'
              }`}
            >
              <div className="space-y-0.5">
                <span className="font-bold text-xs block">Sí, traigo Teorías Psicológicas aprobada</span>
                <span className="text-[11px] text-slate-500 block">
                  Acredita automáticamente COD 400 (5 CR) y COD 401 (5 CR).
                </span>
              </div>
              {esPlan1988 && <CheckCircle2 size={18} className="text-[#0099A9] shrink-0" />}
            </div>
          </div>
        </div>

        {/* Botones de Acción */}
        <div className="flex items-center gap-3 pt-2">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="w-1/3 py-3 px-4 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-bold text-xs transition-all cursor-pointer"
            >
              Atrás
            </button>
          )}

          <button
            type="submit"
            className="flex-1 py-3 px-6 rounded-2xl bg-[#0099A9] hover:bg-[#007683] text-white font-bold text-xs flex items-center justify-center gap-2 shadow-md transition-all cursor-pointer active:scale-95"
          >
            <span>Generar Mi Malla 2013</span>
            <ArrowRight size={16} />
          </button>
        </div>
      </form>
    </div>
  );
};
